import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { X, LogOut, Trash2, Loader2, AlertTriangle, Route as RouteIcon, Bell, Car, UserRound, RefreshCw, ChevronDown, ChevronRight, CreditCard, HelpCircle, LifeBuoy, Wallet, Gift, Star, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ProfilePhotoManager from "@/components/price/ProfilePhotoManager";
import StripeConnectCard from "@/components/price/StripeConnectCard";
import PriceWalletCard from "@/components/price/PriceWalletCard";
import IdentityStatusCard from "@/components/price/IdentityStatusCard";
import DriverDocsCard from "@/components/price/DriverDocsCard";
import DriverVehiclesCard from "@/components/price/DriverVehiclesCard";
import { clearToken } from "@/lib/tokenStore";
import CguViewerDialog from "@/components/price/CguViewerDialog";
import { useAuth } from "@/context/AuthContext";

/**
 * Price — Sheet "Mon compte" ouvert depuis la topbar des dashboards.
 *
 * Regroupe :
 *   - Ma photo de profil (upload + statut modération)
 *   - Stripe Connect (uniquement covoitureur : configuration IBAN)
 *   - Mes trajets (historique)
 *   - Déconnexion
 *   - Suppression de compte
 *
 * Props :
 *   - open     : booléen d'affichage
 *   - onClose  : callback fermeture
 *   - variant  : "driver" | "client" — décide quels blocs afficher
 *   - onLogout : callback déconnexion
 *   - onHistory: callback pour ouvrir la feuille d'historique
 *   - displayName / displayEmail : entête sheet
 */
export default function PriceProfileSheet({ open, onClose, variant = "client", onLogout, onHistory, displayName, displayEmail }) {
  const [cguOpen, setCguOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [notifPrefsOpen, setNotifPrefsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const { user, profile, refresh } = useAuth();
  const [section, setSection] = useState("profile");
  const [walletOpen, setWalletOpen] = useState(false);
  const nav = useNavigate();

  const linkedRoles = Array.isArray(user?.linked_roles) ? user.linked_roles : [];
  const hasDriverProfile = linkedRoles.includes("driver") || user?.role === "driver";
  const [driverApproved, setDriverApproved] = useState(false);
  const hasClientProfile = linkedRoles.includes("client") || user?.role === "client" || driverApproved;

  useEffect(() => {
    if (!open || variant !== "driver") return;
    api.get("/price/me/mode-status")
      .then(({ data }) => setDriverApproved(data?.driver_profile?.status === "approved"))
      .catch(() => {});
  }, [open, variant]);

  const switchMode = async (targetMode) => {
    if (switching) return;
    setSwitching(true);
    try {
      await api.post("/price/me/switch-mode", { mode: targetMode });
      try { await refresh?.(); } catch { /* best-effort */ }
      onClose?.();
      nav(targetMode === "driver" ? "/dashboard/driver" : "/dashboard/client", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de basculer");
    } finally {
      setSwitching(false);
    }
  };
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setDeleteOpen(false);
      setNotifPrefsOpen(false);
      setSection("profile");
      setWalletOpen(false);
    }
  }, [open]);

  if (!open) return null;

  const isDriver = variant === "driver";
  const initials = (displayName || "P").trim().slice(0, 1).toUpperCase();
  const close = () => onClose?.();

  return (
    <div
      className="fixed inset-0 z-[46] overflow-y-auto bg-[#FFF8F1] animate-in fade-in duration-200"
      data-testid="price-profile-sheet"
      style={{ paddingTop: "max(16px, env(safe-area-inset-top))", paddingBottom: "calc(110px + env(safe-area-inset-bottom))" }}
    >
      <div className="mx-auto w-full max-w-md px-5">
        <div className="flex items-center justify-between">
          <h1 className="font-[Outfit] text-[30px] font-black leading-none">Réglages</h1>
          <button type="button" data-testid="profile-sheet-close" onClick={close} aria-label="Fermer"
            className="street-press grid h-10 w-10 place-items-center rounded-full bg-white street-border-thin street-shadow-sm">
            <X className="h-4 w-4" strokeWidth={3} />
          </button>
        </div>

        <div className="mt-5 flex items-center gap-3 rounded-3xl bg-white p-4 street-border street-shadow-sm" data-testid="settings-identity">
          <div className="grid h-14 w-14 flex-none place-items-center overflow-hidden rounded-full bg-primary text-primary-foreground street-border-thin">
            {profile?.photo_url ? <img src={profile.photo_url} alt="" className="h-full w-full object-cover" /> : <span className="font-[Outfit] text-[22px] font-black">{initials}</span>}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-[Outfit] text-[18px] font-black leading-tight">{displayName || "Mon compte"}</div>
            <div className="truncate text-[12px] font-semibold text-foreground/60">
              {isDriver && profile?.vehicle_brand_model ? `${profile.vehicle_brand_model}${profile.license_plate ? ` · ${profile.license_plate}` : ""}` : displayEmail}
            </div>
          </div>
        </div>

        <SettingsTiles isDriver={isDriver} onWallet={() => setWalletOpen((v) => !v)} onShowWallet={() => setWalletOpen(true)} onPayments={() => setSection("payments")} />
        {walletOpen && <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-200"><PriceWalletCard /></div>}

        <div className="mt-5 space-y-3">
          <SettingsAccordion id="profile" title="Mon profil" Icon={UserRound} section={section} setSection={setSection}>
            <ProfilePhotoManager />
            <IdentityStatusCard />
            {isDriver && <div className="pt-1"><div className="mb-2 font-[Outfit] text-[14px] font-black">Mes documents</div><DriverDocsCard /></div>}
            {isDriver && <div className="pt-1"><div className="mb-2 font-[Outfit] text-[14px] font-black">Mes véhicules</div><DriverVehiclesCard /></div>}
            {variant === "client" && !hasDriverProfile && (
              <SettingsRow testid="profile-sheet-become-driver" Icon={Car} label="Devenir aussi conducteur" sub="Garde ton compte passager et publie des trajets" onClick={() => { close(); nav("/become-driver"); }} />
            )}
            {variant === "client" && hasDriverProfile && (
              <SettingsRow testid="profile-sheet-switch-driver" Icon={switching ? Loader2 : RefreshCw} spin={switching} label="Passer en mode conducteur" sub="Bascule vers ton tableau conducteur" onClick={() => switchMode("driver")} />
            )}
            {isDriver && hasClientProfile && (
              <SettingsRow testid="profile-sheet-switch-client" Icon={switching ? Loader2 : UserRound} spin={switching} label="Passer en mode passager" sub="Bascule vers ton tableau passager" onClick={() => switchMode("client")} />
            )}
          </SettingsAccordion>

          {isDriver && (
            <SettingsAccordion id="payments" title="Paiements" Icon={CreditCard} section={section} setSection={setSection}>
              <StripeConnectCard />
            </SettingsAccordion>
          )}

          <SettingsAccordion id="notifs" title="Notifications" Icon={Bell} section={section} setSection={setSection}>
            <SettingsRow testid="profile-sheet-notif-prefs" Icon={Bell} label="Sons des notifications" sub="Silence, son système ou son Price par catégorie" onClick={() => setNotifPrefsOpen(true)} />
          </SettingsAccordion>

          {onHistory && (
            <SettingsAccordion id="history" title={isDriver ? "Historique des courses" : "Historique des trajets"} Icon={RouteIcon} section={section} setSection={setSection}>
              <SettingsRow testid="profile-sheet-history" Icon={RouteIcon} label="Mes trajets" sub="Toutes les courses terminées" onClick={() => { close(); onHistory(); }} />
            </SettingsAccordion>
          )}

          {!isDriver && (
            <SettingsAccordion id="help" title="Aide et support" Icon={HelpCircle} section={section} setSection={setSection}>
              <SettingsRow testid="profile-sheet-support" Icon={LifeBuoy} label="Écrire au support Price" sub="L'équipe te répond dans l'app" onClick={() => { close(); window.dispatchEvent(new Event("price:open-support")); }} />
            </SettingsAccordion>
          )}

          <SettingsAccordion id="account" title="Compte" Icon={UserRound} section={section} setSection={setSection}>
            <SettingsRow testid="profile-sheet-logout" Icon={LogOut} label="Déconnexion" sub="Fermer la session sur cet appareil" onClick={onLogout} />
            <SettingsRow testid="profile-sheet-cgu" Icon={FileText} label="Conditions Générales d'Utilisation" sub="Version en vigueur et date de ton acceptation" onClick={() => setCguOpen(true)} />
            <SettingsRow testid="profile-sheet-delete-open" Icon={Trash2} danger label="Supprimer mon compte" sub="Effacer définitivement mon compte et mes données" onClick={() => setDeleteOpen(true)} />
          </SettingsAccordion>
        </div>
      </div>

      {deleteOpen && createPortal(
        <DeleteAccountDialog
          onClose={() => setDeleteOpen(false)}
          onDeleted={async () => {
            setDeleteOpen(false);
            await clearToken();
            try { window.location.assign("/"); } catch {/*noop*/}
          }}
        />, document.body,
      )}

      {cguOpen && <CguViewerDialog withAcceptance onClose={() => setCguOpen(false)} />}
      {notifPrefsOpen && createPortal(<NotifSoundPrefsDialog onClose={() => setNotifPrefsOpen(false)} />, document.body)}
    </div>
  );
}

function SettingsTiles({ isDriver, onWallet, onShowWallet, onPayments }) {
  const [w, setW] = useState(null);
  const [stripe, setStripe] = useState(null);
  useEffect(() => {
    api.get("/price/wallet").then(({ data }) => setW(data)).catch(() => {});
    if (isDriver) api.get("/price/driver/stripe/connect/status").then(({ data }) => setStripe(data)).catch(() => {});
  }, [isDriver]);
  const eur = (v) => (w ? `${Number(v || 0).toFixed(2).replace(".", ",")} €` : "…");
  const l = w?.loyalty || {};
  const tiles = isDriver
    ? [
        { key: "wallet", label: "Cagnotte", value: eur(w?.balance_eur), Icon: Wallet, onClick: onWallet, tone: "bg-primary text-primary-foreground" },
        { key: "stripe", label: "Versements", value: stripe?.charges_enabled && stripe?.payouts_enabled ? "Actif" : "À configurer", Icon: CreditCard, onClick: onPayments, tone: "bg-white" },
      ]
    : [
        { key: "wallet", label: "Cagnotte", value: eur(w?.balance_eur), Icon: Wallet, onClick: onWallet, tone: "bg-primary text-primary-foreground" },
        { key: "loyalty", label: "Fidélité", value: `${Number(l.trips || 0)}/${Number(l.goal_trips || 5)}`, Icon: Gift, onClick: onShowWallet, tone: "bg-accent text-accent-foreground" },
        { key: "referral", label: "Parrainage", value: "Inviter", Icon: Star, onClick: onShowWallet, tone: "bg-white" },
      ];
  return (
    <div className={`mt-4 grid gap-2.5 ${isDriver ? "grid-cols-2" : "grid-cols-3"}`} data-testid="settings-tiles">
      {tiles.map(({ key, label, value, Icon, onClick, tone }) => (
        <button key={key} type="button" onClick={onClick} data-testid={`settings-tile-${key}`}
          className={`street-press flex flex-col items-start gap-2 rounded-3xl p-3 text-left street-border street-shadow-sm ${tone}`}>
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-white text-foreground street-border-thin"><Icon className="h-4 w-4" strokeWidth={2.75} /></span>
          <span className="text-[11px] font-black uppercase tracking-wider opacity-80">{label}</span>
          <span className="font-[Outfit] text-[16px] font-black leading-none">{value}</span>
        </button>
      ))}
    </div>
  );
}

function SettingsAccordion({ id, title, Icon, section, setSection, children }) {
  const open = section === id;
  return (
    <div className="overflow-hidden rounded-3xl bg-white street-border street-shadow-sm" data-testid={`settings-acc-${id}`}>
      <button type="button" onClick={() => setSection(open ? null : id)} className="flex w-full items-center gap-3 p-4 text-left" data-testid={`settings-acc-${id}-toggle`} aria-expanded={open}>
        <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-[#FFE9D6] street-border-thin"><Icon className="h-5 w-5" strokeWidth={2.5} /></span>
        <span className="flex-1 font-[Outfit] text-[16px] font-black">{title}</span>
        <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} strokeWidth={3} />
      </button>
      {open && <div className="space-y-3 border-t-2 border-black/10 px-3 pb-3 pt-3 animate-in fade-in slide-in-from-top-1 duration-200">{children}</div>}
    </div>
  );
}

function SettingsRow({ testid, Icon, label, sub, onClick, danger, spin }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="street-press flex w-full items-center gap-3 rounded-2xl bg-[#FFF8F1] p-3 text-left street-border-thin">
      <span className={`grid h-10 w-10 flex-none place-items-center rounded-xl street-border-thin ${danger ? "bg-[#3B0F0F] text-white" : "bg-white"}`}>
        <Icon className={`h-5 w-5 ${spin ? "animate-spin" : ""}`} strokeWidth={2.5} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block font-[Outfit] text-[14px] font-black leading-tight ${danger ? "text-[#C81E1E]" : ""}`}>{label}</span>
        {sub && <span className="mt-0.5 block truncate text-[11px] font-medium text-foreground/60">{sub}</span>}
      </span>
      <ChevronRight className="h-4 w-4 flex-none text-foreground/40" strokeWidth={3} />
    </button>
  );
}

/* -------------------------------------------------------------------------
 * Modal de préférences son notifications.
 *
 * Permet à l'utilisateur de choisir, pour chaque catégorie, entre :
 *   • Son Price (par défaut)
 *   • Son système
 *   • Silencieux
 * Avec un override global qui s'applique à toutes les catégories non
 * personnalisées. Backend : GET/PUT /price/me/notif-prefs.
 * ------------------------------------------------------------------------- */
const _NOTIF_CATEGORIES = [
  { key: "ride",     label: "Courses",             help: "Demande, acceptation, rappel" },
  { key: "chat",     label: "Messages",            help: "Nouveau message dans un chat" },
  { key: "update",   label: "Mises à jour",        help: "Nouvelle version de l'app" },
  { key: "promo",    label: "Promos & offres",     help: "Codes promo, boost gains" },
  { key: "withdraw", label: "Retraits",            help: "Confirmation virement Stripe" },
  { key: "survey",   label: "Sondages",            help: "Questions de l'admin Price" },
];
const _NOTIF_SOUND_CHOICES = [
  { value: "price",  emoji: "🎵", label: "Son Price" },
  { value: "system", emoji: "🔔", label: "Son système" },
  { value: "silent", emoji: "🔕", label: "Silencieux" },
];

function NotifSoundPrefsDialog({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({
    global: "price",
    chat: null, ride: null, update: null, promo: null, withdraw: null, survey: null,
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/price/me/notif-prefs");
        setPrefs({
          global: data?.global || "price",
          chat: data?.chat || null,
          ride: data?.ride || null,
          update: data?.update || null,
          promo: data?.promo || null,
          withdraw: data?.withdraw || null,
          survey: data?.survey || null,
        });
      } catch (_e) {
        // best-effort — on garde les defaults
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/price/me/notif-prefs", prefs);
      toast.success("Préférences enregistrées");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const setCat = (key, value) => setPrefs((p) => ({ ...p, [key]: value }));

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 overflow-y-auto"
      onClick={onClose}
      data-testid="notif-prefs-dialog"
    >
      <div
        className="min-h-full flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full max-w-md rounded-t-3xl sm:rounded-2xl bg-[#FFF8F1] border-2 border-black shadow-[0_6px_0_0_#000] max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 z-10 flex items-center justify-between gap-3 bg-[#FFF8F1] px-5 pt-5 pb-3 border-b-2 border-black/10">
            <div className="min-w-0">
              <div className="font-[Outfit] text-[18px] font-black leading-none">Sons des notifications</div>
              <div className="mt-1 text-[11px] text-foreground/60">Change à tout moment — ça s'applique immédiatement</div>
            </div>
            <button
              onClick={onClose}
              data-testid="notif-prefs-close"
              className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-white border-2 border-black active:translate-y-[1px]"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" strokeWidth={3} />
            </button>
          </div>

          {loading ? (
            <div className="p-10 grid place-items-center">
              <Loader2 className="h-6 w-6 animate-spin text-foreground/40" />
            </div>
          ) : (
            <div className="p-5 space-y-4">
              {/* Global */}
              <div className="rounded-2xl bg-primary/5 p-3 border-2 border-black">
                <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60 leading-none">
                  Son global (défaut)
                </div>
                <div className="mt-1 text-[11px] text-foreground/50">S'applique à toute catégorie non personnalisée ci-dessous</div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {_NOTIF_SOUND_CHOICES.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setCat("global", c.value)}
                      data-testid={`notif-global-${c.value}`}
                      className={`rounded-xl px-2 py-2.5 text-center border-2 border-black transition-all active:translate-y-[1px] ${
                        prefs.global === c.value
                          ? "bg-primary text-primary-foreground shadow-[0_3px_0_0_#000]"
                          : "bg-white"
                      }`}
                    >
                      <div className="text-lg leading-none">{c.emoji}</div>
                      <div className="mt-1 text-[10px] font-black leading-none">{c.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Catégories */}
              {_NOTIF_CATEGORIES.map((cat) => (
                <div key={cat.key} className="rounded-2xl bg-white p-3 border-2 border-black">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-[Outfit] text-[13px] font-black leading-none">{cat.label}</div>
                      <div className="mt-0.5 text-[11px] text-foreground/50 truncate">{cat.help}</div>
                    </div>
                    {prefs[cat.key] == null && (
                      <div className="text-[9px] font-black uppercase tracking-widest text-foreground/40 flex-none">
                        Suit le global
                      </div>
                    )}
                  </div>
                  <div className="mt-2.5 grid grid-cols-4 gap-1.5">
                    <button
                      onClick={() => setCat(cat.key, null)}
                      data-testid={`notif-${cat.key}-inherit`}
                      className={`rounded-lg px-2 py-1.5 text-[10px] font-black border-2 border-black transition-all active:translate-y-[1px] ${
                        prefs[cat.key] == null ? "bg-primary text-primary-foreground" : "bg-white"
                      }`}
                    >
                      ↻ Global
                    </button>
                    {_NOTIF_SOUND_CHOICES.map((c) => (
                      <button
                        key={c.value}
                        onClick={() => setCat(cat.key, c.value)}
                        data-testid={`notif-${cat.key}-${c.value}`}
                        className={`rounded-lg px-2 py-1.5 text-[10px] font-black border-2 border-black transition-all active:translate-y-[1px] flex items-center justify-center gap-1 ${
                          prefs[cat.key] === c.value
                            ? "bg-primary text-primary-foreground"
                            : "bg-white"
                        }`}
                      >
                        <span>{c.emoji}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="sticky bottom-0 bg-[#FFF8F1] px-5 py-4 border-t-2 border-black/10">
            <button
              onClick={save}
              disabled={saving || loading}
              data-testid="notif-prefs-save"
              className="w-full rounded-2xl bg-primary text-primary-foreground font-black py-3.5 border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-60"
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Boîte de dialogue de suppression compte — demande le mot de passe et
 * la saisie explicite "SUPPRIMER" pour éviter tout clic accidentel.
 * L'API supprime les données personnelles ET anonymise les traces qui
 * doivent rester pour l'autre partie (comptables, notes reçues).
 */
export function DeleteAccountDialog({ onClose, onDeleted }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [category, setCategory] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const REASONS = ["Pas assez de conducteurs / trajets", "Prix trop élevés", "Problème avec un conducteur ou un passager",
    "Application difficile à utiliser", "Bugs ou problèmes techniques", "Je n'en ai plus besoin", "Autre"];

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    if (!category) return toast.error("Choisis la raison de ton départ");
    if (reason.trim().length < 5) return toast.error("Explique-nous en quelques mots pourquoi tu pars");
    if (!password) return toast.error("Mot de passe requis");
    if (confirmation.trim().toUpperCase() !== "SUPPRIMER") {
      return toast.error("Tape SUPPRIMER pour confirmer");
    }
    setBusy(true);
    try {
      await api.post("/auth/me/delete", { password, confirmation: "SUPPRIMER", reason_category: category, reason: reason.trim() });
      toast.success("Compte supprimé. Au revoir.");
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-testid="delete-account-dialog"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-white rounded-3xl border-2 border-black shadow-[0_8px_0_0_#000] overflow-hidden"
      >
        <div className="bg-[#3B0F0F] px-5 py-3 border-b-2 border-black flex items-center gap-3">
          <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-white border-2 border-black">
            <AlertTriangle className="h-5 w-5 text-[#FF3B3B]" strokeWidth={2.75} />
          </div>
          <div className="min-w-0">
            <div className="font-[Outfit] text-[10px] font-black uppercase tracking-widest text-white/70">Action définitive</div>
            <div className="font-[Outfit] text-[16px] font-black text-white leading-tight">Supprimer mon compte</div>
          </div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-[13px] leading-snug text-neutral-800">
            Cette action est <b>irréversible</b>. Ton compte, ta photo, ta liste
            de trajets et tes conversations seront supprimés définitivement.
            Les traces comptables (notes reçues, trajets passés) seront
            anonymisées pour respecter les obligations légales.
          </p>
          <div data-testid="delete-account-reasons">
            <label className="block text-[11px] font-black uppercase tracking-widest text-neutral-600 mb-1">
              Pourquoi pars-tu ? <span className="text-[#FF3B3B]">*</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {REASONS.map((r, i) => (
                <button key={r} type="button" onClick={() => setCategory(r)} data-testid={`delete-account-reason-${i}`}
                  className={`rounded-full border-2 border-black px-2.5 py-1 text-[11px] font-black transition-colors ${category === r ? "bg-[#FF3B3B] text-white" : "bg-white"}`}>
                  {r}
                </button>
              ))}
            </div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={1000}
              placeholder="Dis-nous ce qu'on pourrait améliorer (obligatoire)" data-testid="delete-account-reason-text"
              className="mt-2 w-full rounded-xl border-2 border-black px-3 py-2 font-medium outline-none focus:bg-[#FFF8F1]" style={{ fontSize: "16px" }} />
          </div>
          <div>
            <label className="block text-[11px] font-black uppercase tracking-widest text-neutral-600 mb-1">
              Ton mot de passe
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="delete-account-password"
              className="w-full rounded-xl border-2 border-black px-3 py-2 text-[15px] font-medium outline-none focus:bg-[#FFF8F1]"
              style={{ fontSize: "16px" }}
            />
          </div>
          <div>
            <label className="block text-[11px] font-black uppercase tracking-widest text-neutral-600 mb-1">
              Écris <span className="text-[#FF3B3B]">SUPPRIMER</span> pour confirmer
            </label>
            <input
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder="SUPPRIMER"
              autoCapitalize="characters"
              data-testid="delete-account-confirmation"
              className="w-full rounded-xl border-2 border-black px-3 py-2 text-[15px] font-black uppercase tracking-wider outline-none focus:bg-[#FFF8F1]"
              style={{ fontSize: "16px" }}
            />
          </div>
        </div>
        <div className="border-t-2 border-black bg-[#FFF8F1] px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="delete-account-cancel"
            className="flex-1 h-11 rounded-xl bg-white text-black font-[Outfit] font-black text-[13px] uppercase tracking-wider border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="delete-account-confirm"
            className="flex-[1.4] h-11 rounded-xl bg-[#3B0F0F] text-white font-[Outfit] font-black text-[13px] uppercase tracking-wider border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-70 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Supprimer définitivement
          </button>
        </div>
      </form>
    </div>
  );
}
